import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Stepper from "./Stepper";
import StepDados from "./StepDados";
import StepEndereco from "./StepEndereco";
import StepRecebimento from "./StepRecebimento";
import StepConferencia from "./StepConferencia";
import OrderSummaryCard from "./OrderSummaryCard";
import CheckoutSuccess from "./CheckoutSuccess";
import PrivacyModal from "@/components/feature/PrivacyModal";
import { useStore } from "@/store/StoreContext";
import {
  calcDiscount,
  formatPhoneBR,
  hasErrors,
  resolveShipping,
  validateStep,
  type CheckoutErrors,
} from "@/lib/commerce";
import { createOrder, upsertCustomer } from "@/lib/records";
import { buildOrderMessage, waLink } from "@/lib/whatsapp";
import { deliveryMethodById } from "@/config/storeConfig";
import type { CartItem, CheckoutForm, CustomerRecord, DeliveryMethodId, OrderRecord } from "@/types";

const STEPS = ["Seus dados", "Endereço", "Recebimento", "Conferência"];

const STEP_BY_FIELD: Record<string, number> = {
  name: 0,
  phone: 0,
  email: 0,
  city: 0,
  state: 0,
  consent: 0,
  cep: 1,
  street: 1,
  number: 1,
  district: 1,
  deliveryMethod: 2,
  destinationCity: 2,
  destinationState: 2,
  terminal: 2,
  responsibleName: 2,
  responsiblePhone: 2,
};

const initialForm: CheckoutForm = {
  name: "",
  phone: "",
  email: "",
  city: "",
  state: "",
  consent: false,
  notes: "",
  deliveryMethod: "",
  address: {
    cep: "",
    street: "",
    number: "",
    complement: "",
    district: "",
    city: "",
    state: "",
    reference: "",
  },
  bus: {
    destinationCity: "",
    destinationState: "",
    terminal: "",
    responsibleName: "",
    responsiblePhone: "",
  },
};

interface CheckoutFlowProps {
  items: CartItem[];
}

export default function CheckoutFlow({ items }: CheckoutFlowProps) {
  const navigate = useNavigate();
  const { clearCart } = useStore();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<CheckoutForm>(initialForm);
  const [errors, setErrors] = useState<CheckoutErrors>({});
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [result, setResult] = useState<{ order: OrderRecord; customer: CustomerRecord } | null>(null);

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0),
    [items],
  );
  const discount = useMemo(() => calcDiscount(subtotal), [subtotal]);
  const shipping = useMemo(
    () => resolveShipping(form.deliveryMethod, subtotal, form),
    [form, subtotal],
  );
  const total = useMemo(
    () => subtotal - discount + (shipping.fee ?? 0),
    [subtotal, discount, shipping],
  );

  const handleField = (name: string, value: string | boolean) => {
    setForm((prev) => {
      if (name === "phone" && typeof value === "string") {
        return { ...prev, phone: formatPhoneBR(value) };
      }
      if (name === "consent" && typeof value === "boolean") {
        return { ...prev, consent: value };
      }
      const next = { ...prev, [name]: value } as CheckoutForm;
      if (name === "city" && !prev.address.city) next.address.city = String(value);
      if (name === "state" && !prev.address.state) next.address.state = String(value);
      return next;
    });
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddress = (name: keyof CheckoutForm["address"], value: string) => {
    setForm((prev) => ({ ...prev, address: { ...prev.address, [name]: value } }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleBus = (name: keyof CheckoutForm["bus"], value: string) => {
    setForm((prev) => ({ ...prev, bus: { ...prev.bus, [name]: value } }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleMethod = (id: DeliveryMethodId) => {
    setForm((prev) => {
      const method = deliveryMethodById(id);
      const address = { ...prev.address };
      if (method?.requiresAddress || method?.requiresCep) {
        if (!address.city) address.city = prev.city;
        if (!address.state) address.state = prev.state;
      }
      return { ...prev, deliveryMethod: id, address };
    });
    if (errors.deliveryMethod) setErrors((prev) => ({ ...prev, deliveryMethod: "" }));
  };

  const goNext = () => {
    const stepErrors = validateStep(step, form);
    if (hasErrors(stepErrors)) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setStep((prev) => Math.min(STEPS.length - 1, prev + 1));
  };

  const goBack = () => {
    setErrors({});
    setStep((prev) => Math.max(0, prev - 1));
  };

  const confirmOrder = () => {
    const merged: CheckoutErrors = {
      ...validateStep(0, form),
      ...validateStep(1, form),
      ...validateStep(2, form),
    };

    if (hasErrors(merged)) {
      const target = Object.keys(merged).reduce((min, field) => {
        const fieldStep = STEP_BY_FIELD[field] ?? 0;
        return fieldStep < min ? fieldStep : min;
      }, STEPS.length);
      setErrors(merged);
      setStep(target);
      return;
    }

    const nowIso = new Date().toISOString();
    const { customer } = upsertCustomer(form, total, nowIso);
    const method = form.deliveryMethod ? deliveryMethodById(form.deliveryMethod) : undefined;
    const showAddress = Boolean(method && method.id !== "retirada" && method.id !== "onibus");

    const order = createOrder({
      customer,
      items,
      subtotal,
      discount,
      shippingFee: shipping.fee,
      total,
      deliveryMethodId: form.deliveryMethod,
      deliveryLabel: method?.name ?? "A combinar",
      deliveryDeadline: method?.deadline ?? "A combinar no atendimento",
      freeShipping: shipping.freeApplied,
      address: showAddress ? form.address : null,
      bus: method?.requiresBus ? form.bus : null,
      notes: form.notes,
      createdAt: nowIso,
    });

    const message = buildOrderMessage(order, form);
    window.open(waLink(message), "_blank", "noopener,noreferrer");

    setResult({ order, customer });
    clearCart();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (result) {
    return (
      <CheckoutSuccess
        order={result.order}
        customer={result.customer}
        onContinue={() => navigate("/")}
      />
    );
  }

  return (
    <div className="w-full bg-background-50">
      <div className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-14">
        <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <button
            type="button"
            onClick={() => navigate("/sacola")}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap transition-colors hover:text-primary-600"
          >
            <i className="ri-arrow-left-line text-base" />
            Voltar para a sacola
          </button>
        </div>

        <h1 className="mt-4 font-heading text-3xl font-medium text-foreground-900 md:text-4xl">
          Finalizar pedido
        </h1>
        <p className="mt-2 max-w-xl text-sm text-foreground-600">
          Vamos com calma, uma etapa por vez. No fim, o pedido completo segue para a gente pelo
          WhatsApp.
        </p>

        <div className="mt-6">
          <Stepper current={step} steps={STEPS} />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1.5fr_1fr] lg:gap-10">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              {step === 0 ? (
                <StepDados
                  form={form}
                  errors={errors}
                  onChange={handleField}
                  onOpenPrivacy={() => setPrivacyOpen(true)}
                />
              ) : null}
              {step === 1 ? (
                <StepEndereco
                  form={form}
                  errors={errors}
                  onChange={handleField}
                  onAddress={handleAddress}
                />
              ) : null}
              {step === 2 ? (
                <StepRecebimento
                  form={form}
                  errors={errors}
                  shipping={shipping}
                  subtotal={subtotal}
                  onChange={handleField}
                  onBus={handleBus}
                  onMethod={handleMethod}
                />
              ) : null}
              {step === 3 ? (
                <StepConferencia
                  form={form}
                  items={items}
                  subtotal={subtotal}
                  discount={discount}
                  shipping={shipping}
                  total={total}
                  onEdit={(target) => {
                    setErrors({});
                    setStep(target);
                  }}
                />
              ) : null}
            </div>

            <div className="sticky bottom-0 z-20 mt-5 flex flex-col gap-3 border-t border-background-200 bg-background-50/95 py-4 backdrop-blur sm:flex-row-reverse sm:items-center sm:justify-between md:static md:border-0 md:bg-transparent md:backdrop-blur-none">
              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={goNext}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
                >
                  Continuar
                  <i className="ri-arrow-right-line text-base" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={confirmOrder}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
                >
                  <i className="ri-whatsapp-line text-lg" />
                  Confirmar e enviar pelo WhatsApp
                </button>
              )}

              {step > 0 ? (
                <button
                  type="button"
                  onClick={goBack}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 sm:w-auto"
                >
                  <i className="ri-arrow-left-line text-base" />
                  {step === STEPS.length - 1 ? "Voltar e corrigir" : "Voltar"}
                </button>
              ) : null}
            </div>
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start">
            <OrderSummaryCard
              items={items}
              subtotal={subtotal}
              discount={discount}
              shipping={shipping}
              total={total}
              deliveryMethodId={form.deliveryMethod}
            />
          </div>
        </div>
      </div>

      <PrivacyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
    </div>
  );
}